import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { eventsForDate, dateToStr, strToDate, todayStr } from "@/lib/timeUtils";
import { Copy, X, ArrowRight, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function DuplicateDayDialog({ open, onClose, sourceDateStr }) {
  const { events, eventsApi, settings } = useApp();
  const weekStart = settings?.week_start || "mon";
  const [viewMonth, setViewMonth] = useState(() => strToDate(sourceDateStr));
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const sourceEvents = useMemo(() => eventsForDate(events, sourceDateStr), [events, sourceDateStr]);

  const dow = weekStart === "mon" ? ["M", "T", "W", "T", "F", "S", "S"] : ["S", "M", "T", "W", "T", "F", "S"];
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const first = new Date(year, month, 1);
  let offset = first.getDay();
  if (weekStart === "mon") offset = (offset + 6) % 7;
  const gridStart = new Date(year, month, 1 - offset);
  const cells = Array.from({ length: 42 }).map((_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
  const today = todayStr();

  const toggle = (ds) => {
    if (ds === sourceDateStr) return;
    setSelected((prev) => { const n = new Set(prev); n.has(ds) ? n.delete(ds) : n.add(ds); return n; });
  };

  const selectAllWeekdays = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      cells.forEach((d) => { if (d.getMonth() === month && d.getDay() >= 1 && d.getDay() <= 5 && dateToStr(d) !== sourceDateStr) n.add(dateToStr(d)); });
      return n;
    });
  };

  const totalNew = sourceEvents.length * selected.size;

  const confirm = async () => {
    if (totalNew === 0) return;
    setBusy(true);
    const list = [];
    for (const ds of selected) {
      for (const ev of sourceEvents) {
        list.push({
          title: ev.title, description: ev.description || "", date: ds,
          start_minutes: ev.start_minutes, end_minutes: ev.end_minutes,
          category_id: ev.category_id, color: ev.color, icon: ev.icon,
          location: ev.location || "", notes: ev.notes || "",
          recurrence: "none", reminder: ev.reminder ?? null, completed: false,
        });
      }
    }
    try {
      await eventsApi.addBulk(list);
      toast.success(`Copied ${sourceEvents.length} events to ${selected.size} day${selected.size > 1 ? "s" : ""}`);
      setSelected(new Set());
      onClose();
    } catch { toast.error("Could not copy the day"); }
    finally { setBusy(false); }
  };

  const srcLabel = strToDate(sourceDateStr).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
  const navMonth = (dir) => setViewMonth(new Date(year, month + dir, 1));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 border-0 gap-0 overflow-hidden" style={{ background: "transparent", boxShadow: "none" }}>
        <DialogTitle className="sr-only">Copy Day</DialogTitle>
        <DialogDescription className="sr-only">Pick the days to copy this day's events onto</DialogDescription>
        <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 320, damping: 26 }}
          className="rounded-3xl overflow-hidden p-5 space-y-4" style={{ background: "var(--bg-sidebar)", border: "1px solid var(--border-subtle)" }} data-testid="duplicate-day-dialog">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "color-mix(in srgb, var(--accent-primary) 20%, transparent)" }}><Copy size={17} style={{ color: "var(--accent-primary)" }} /></div>
              <div>
                <h3 className="font-display text-lg font-bold leading-tight" style={{ color: "var(--text-primary)" }}>Copy This Day</h3>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>{sourceEvents.length} events from {srcLabel}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70" style={{ color: "var(--text-muted)" }}><X size={18} /></button>
          </div>

          {sourceEvents.length === 0 ? (
            <p className="text-sm py-4 text-center" style={{ color: "var(--text-muted)" }}>This day has no events to copy.</p>
          ) : (
            <>
              <p className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Tap the days to copy onto</p>

              {/* month picker */}
              <div className="rounded-2xl p-3" style={{ background: "var(--bg-timeline-track)", border: "1px solid var(--border-subtle)" }}>
                <div className="flex items-center justify-between mb-2">
                  <button onClick={() => navMonth(-1)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:opacity-70" style={{ color: "var(--text-secondary)" }} data-testid="dup-prev-month"><ChevronLeft size={16} /></button>
                  <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{MONTHS[month]} {year}</span>
                  <button onClick={() => navMonth(1)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:opacity-70" style={{ color: "var(--text-secondary)" }} data-testid="dup-next-month"><ChevronRight size={16} /></button>
                </div>
                <div className="grid grid-cols-7 mb-1">
                  {dow.map((d, i) => <span key={i} className="text-center text-[10px] font-semibold" style={{ color: "var(--text-muted)" }}>{d}</span>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {cells.map((d, i) => {
                    const ds = dateToStr(d);
                    const inMonth = d.getMonth() === month;
                    const isSource = ds === sourceDateStr;
                    const isSel = selected.has(ds);
                    const isToday = ds === today;
                    return (
                      <button key={i} onClick={() => toggle(ds)} disabled={isSource}
                        className="h-8 rounded-lg text-xs font-medium tnum transition-all relative"
                        style={{
                          background: isSel ? "var(--accent-primary)" : isSource ? "color-mix(in srgb, var(--accent-primary) 18%, transparent)" : "transparent",
                          color: isSel ? "#fff" : isSource ? "var(--accent-primary)" : inMonth ? "var(--text-primary)" : "var(--text-muted)",
                          opacity: inMonth ? 1 : 0.4,
                          border: isToday && !isSel ? "1px solid var(--border-active)" : "1px solid transparent",
                          cursor: isSource ? "not-allowed" : "pointer",
                        }}
                        data-testid={`dup-day-${ds}`}>
                        {d.getDate()}
                        {isSource && <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full" style={{ background: "var(--accent-primary)" }} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button onClick={selectAllWeekdays} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ background: "var(--bg-timeline-track)", color: "var(--text-secondary)", border: "1px solid var(--border-subtle)" }} data-testid="dup-select-weekdays">+ Weekdays this month</button>
                {selected.size > 0 && <button onClick={() => setSelected(new Set())} className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ color: "var(--text-muted)" }} data-testid="dup-clear">Clear</button>}
              </div>

              <div className="flex items-center gap-2 text-sm rounded-xl px-3 py-2.5" style={{ background: "color-mix(in srgb, var(--accent-primary) 12%, var(--bg-card))", border: "1px solid var(--border-subtle)" }}>
                <span style={{ color: "var(--text-secondary)" }}>{sourceEvents.length} events</span>
                <ArrowRight size={14} style={{ color: "var(--text-muted)" }} />
                <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{selected.size} day{selected.size !== 1 ? "s" : ""}</span>
                <span className="ml-auto font-mono text-xs px-2 py-0.5 rounded-md" style={{ background: "var(--accent-primary)", color: "#fff" }} data-testid="dup-total-count">+{totalNew}</span>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" onClick={onClose} style={{ color: "var(--text-secondary)" }}>Cancel</Button>
                <Button onClick={confirm} disabled={busy || totalNew === 0} className="text-white font-semibold px-6" style={{ background: "var(--accent-primary)" }} data-testid="dup-confirm">
                  <Check size={16} /> {busy ? "Copying..." : selected.size > 0 ? `Copy to ${selected.size} day${selected.size > 1 ? "s" : ""}` : "Select days"}
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
